import {Component, Object3D} from '@wonderlandengine/api';
import {property} from '@wonderlandengine/api/decorators.js';
import {DefaultGameRules} from './default-game-rules.js';
import {StatNames} from '../../enums/StatNames.js';
import {Puzzle} from '../puzzle.js';
import {Statistics} from '../statistics.js';
import {Localization} from '../../utils/Localization.js';
import {ScoreController} from '../score-controller.js';
import {formatString} from '../../ui/utils/utils.js';
import {leaderboards} from '@wonderlandengine/uber-sdk';
import {timeSinceEpoch} from '../../utils/Utils.js';
import {Analytics, AnalyticsEvent} from '../../classes/Analytics.js';
import {LeaderboardManager} from '../leaderboard-manager.js';
import {GameModeType} from '../../enums/GameModeType.js';
import {Tags} from '../tags.js';
import {WLUtils} from '../../utils/WLUtils.js';
import {BonusCountdownPower} from '../special-powers/bonus-countdown-power.js';
import {TutorialQuickplayMode} from '../tutorial-quickplay-mode.js';
import {UIModel} from '../../models/UIModel.js';
import {BooleanStore} from '../boolean-store.js';
import {BooleanName} from '../../enums/BooleanName.js';

export class QuickPlayGameRules extends DefaultGameRules {
    static TypeName = 'quick-play-game-rules';
    static InheritProperties = true;

    @property.float(1.5)
    startingDropSpeedIncrease = 1.5;

    private addBonusTime = 0;

    private _numStarsCollected = 0;
    private _numFruitDropped;

    get getExtraBonusTime() {
        return this.addBonusTime;
    }

    set getExtraBonusTime(value: number) {
        this.addBonusTime = value;
    }

    private _firstTimePlayingQuickplay = false;

    override startNewGame(): void {
        this._firstTimePlayingQuickplay =
            Statistics.instance.getLong(StatNames.QuickplayGamesPlayed) === 0 &&
            !BooleanStore.instance.get(BooleanName.QuickplayTutorialTutorialPlayed) &&
            !BooleanStore.instance.get(BooleanName.EasyModeTutorialPlayed);

        super.startNewGame();

        this._numStarsCollected = 0;

        this.puzzleController.score.addStarCount(this._numStarsCollected);

        this.puzzleController.score.showQuickplayModeHud(true);

        this._numFruitDropped = 0;

        this.puzzleController.dropSpeedFactor = this.startingDropSpeedIncrease;

        this.puzzleController.score.level = 1;

        this.puzzleController.score.setLevelSlider(1);
        this.puzzleController.score.setQuickplayText(this.levelUpTurns, this.levelUpTurns);

        if (this._firstTimePlayingQuickplay) {
            this.object.getComponent(TutorialQuickplayMode).startTutorial();
        }
    }

    override showFinalResults(): void {
        const combo = this.puzzleController.score.combo;

        this.puzzleController.score.showFinalResults(
            Localization.instance.get('BestChainFormat'),
            combo,
            Localization.instance.get('Quickplay'),
            StatNames.RecentHighScoreQuickplay
        );

        Statistics.instance.recordHighest(
            StatNames.HighStarsCollected,
            this._numStarsCollected
        );

        Statistics.instance.recordAdditive(
            StatNames.TotalStarsCollected,
            this._numStarsCollected
        );

        Statistics.instance.record(StatNames.LastStarsCollected, this._numStarsCollected);
        Statistics.instance.record(
            StatNames.LastFruitLeft,
            this.levelUpTurns - this._numFruitDropped
        );

        Analytics.LogEventWithValue(AnalyticsEvent.StarsPerGame, this._numStarsCollected);
        Analytics.LogEventWithValue(
            AnalyticsEvent.FruitLeftInQuickplay,
            this.levelUpTurns - this._numFruitDropped
        );
    }

    override calculateLevelBar(): void {
        // figure out where level bar should be
        const levelValue = 1.0 - this._turnsSinceLastLevelUp / this._currentLevelUpTurns;
        this.puzzleController.score.setLevelSlider(levelValue);
    }

    override turnComplete(): void {
        super.turnComplete();

        this._numFruitDropped++;
        Puzzle.instance.score.setQuickplayText(
            this.levelUpTurns - this._numFruitDropped,
            this.levelUpTurns
        );

        switch (this.levelUpTurns - this._numFruitDropped) {
            case 1:
                ScoreController.instance.remainingFruitBigText(
                    Localization.instance.get('LastFruit')
                );
                break;
            case 5:
            case 10:
            case 25:
            case 50:
            case 75:
                const format = Localization.instance.get('FruitToGo');
                ScoreController.instance.remainingFruitBigText(
                    formatString(format, this.levelUpTurns - this._numFruitDropped)
                );
                break;
            default:
                break;
        }

        Tags.getObjectsWithTag('BonusCountdown').forEach((obj) => {
            const power = WLUtils.getComponentsOfTypeRecursive(obj, BonusCountdownPower)[0];
            if (power) {
                power.countdown(this.puzzleController);
            }
        });
    }

    override getHighScore(): number {
        return Statistics.instance.getLong(StatNames.HighScoreQuickplay);
    }

    override getWeeklyHighScore(): number {
        return Statistics.instance.getLong(StatNames.RecentHighScoreQuickplay);
    }
    override saveHighScore(score: number): void {
        super.saveHighScore(score);
        Statistics.instance.recordHighest(StatNames.HighScoreQuickplay, score);

        if (Statistics.instance.recordHighest(StatNames.RecentHighScoreQuickplay, score)) {
            Statistics.instance.record(
                StatNames.RecentHighScoreQuickplayTimestamp,
                timeSinceEpoch()
            );
        }

        LeaderboardManager.instance
            .saveScoreToPlatformLeaderboard(score, GameModeType.Quickplay)
            .catch((e) => {
                console.error('Error saving score to platform leaderboard:', e);
            });
    }
    override saveCumulativeScore(score: number): void {
        Statistics.instance.recordAdditive(StatNames.CumulativeScoreQuickplay, score);
        Statistics.instance.recordAdditive(StatNames.CumulativeScoreAny, score);

        let roundedScore = 0;

        if (score > 10000000) {
            roundedScore = score - (score % 1000000);
        } else if (score > 1000000) {
            roundedScore = score - (score % 1000000);
        } else if (score > 100000) {
            roundedScore = score - (score % 100000);
        } else if (score > 10000) {
            roundedScore = score - (score % 10000);
        } else {
            roundedScore = score - (score % 1000);
        }

        //Logger.Log("Score logged " + score + " = " + roundedScore);
        Analytics.LogEventWithValue(AnalyticsEvent.ScoreQuickplay, roundedScore);
    }

    override incrementGamesPlayed() {
        Statistics.instance.recordAdditive(StatNames.QuickplayGamesPlayed, 1);
        Statistics.instance.recordAdditive(StatNames.TotalGamesPlayed, 1);

        // if (GetSpecialMode(SpecialMode.FreezeDrop) == true)
        // {
        // 	Statistics.GetInstance().RecordAdditive(StatNames.FreezeDropGamesPlayed, 1);
        // }
    }

    override beatGame() {
        Statistics.instance.recordAdditive(StatNames.BeatQuickplayMode, 1);
    }

    getNumStars() {
        return this._numStarsCollected;
    }

    increaseNumStars() {
        this._numStarsCollected++;
        Puzzle.instance.score.addStarCount(this._numStarsCollected);
    }
}
